import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Check } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
    const location = useLocation();
    const navigate = useNavigate();
    const { refreshUser } = useAuth();
    const [state, setState] = useState({ status: "polling", tries: 0 });

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const sessionId = params.get("session_id");
        if (!sessionId) { setState({ status: "error", tries: 0 }); return; }

        let cancelled = false;
        let tries = 0;

        const tick = async () => {
            if (cancelled) return;
            tries += 1;
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    await refreshUser();
                    setState({ status: "paid", tries });
                    return;
                }
                if (data.payment_status === "failed" || data.payment_status === "expired") {
                    setState({ status: "failed", tries });
                    return;
                }
            } catch (e) { console.debug("payment status poll retry", e?.message); /* keep polling */ }
            if (tries > 40) { setState({ status: "timeout", tries }); return; }
            setTimeout(tick, 2000);
        };
        tick();
        return () => { cancelled = true; };
    }, [location.search, refreshUser]);

    return (
        <div className="min-h-[60vh] flex items-center justify-center text-center px-6">
            <div className="max-w-lg">
                {state.status === "polling" && (
                    <>
                        <Sparkles className="w-6 h-6 mx-auto text-[hsl(var(--eden-gold))] animate-pulse" strokeWidth={1.2} />
                        <h1 className="font-serif text-4xl md:text-5xl leading-tight mt-6">Confirming your Premium…</h1>
                        <p className="mt-4 text-muted-foreground">Just a moment while Stripe finalizes the payment.</p>
                    </>
                )}
                {state.status === "paid" && (
                    <>
                        <div className="w-16 h-16 mx-auto rounded-full bg-[hsl(var(--eden-gold))]/15 flex items-center justify-center">
                            <Check className="w-7 h-7 text-[hsl(var(--eden-gold))]" strokeWidth={1.5} />
                        </div>
                        <h1 className="font-serif text-4xl md:text-5xl leading-tight mt-6" data-testid="payment-success-heading">Welcome to Premium.</h1>
                        <p className="mt-4 text-muted-foreground">Your full Stewardship Blueprint, Tend, and every deeper feature is now open to you.</p>
                        <div className="mt-8 flex flex-wrap justify-center gap-2">
                            <Button onClick={() => navigate("/blueprint")} className="bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))] hover:bg-[hsl(var(--eden-forest))]/90 rounded-md text-xs uppercase tracking-[0.3em] h-11 px-6">Read my full Blueprint</Button>
                            <Button onClick={() => navigate("/tend")} variant="outline" className="rounded-md border-foreground/15 text-xs uppercase tracking-[0.3em] h-11 px-6">Enter Tend</Button>
                        </div>
                    </>
                )}
                {state.status === "failed" && (
                    <>
                        <h1 className="font-serif text-4xl md:text-5xl leading-tight">Payment couldn't complete.</h1>
                        <p className="mt-4 text-muted-foreground">No charge was made. You can try again anytime.</p>
                        <Button onClick={() => navigate("/membership")} className="mt-8 bg-[hsl(var(--eden-forest))] text-[hsl(var(--eden-cream))] hover:bg-[hsl(var(--eden-forest))]/90 rounded-md text-xs uppercase tracking-[0.3em] h-11 px-6">Return to membership</Button>
                    </>
                )}
                {state.status === "timeout" && (
                    <>
                        <h1 className="font-serif text-4xl md:text-5xl leading-tight">Still confirming…</h1>
                        <p className="mt-4 text-muted-foreground">Stripe is taking a moment. Your subscription may already be active — refresh in a minute.</p>
                        <Button onClick={() => navigate("/membership")} className="mt-8 rounded-md text-xs uppercase tracking-[0.3em] h-11 px-6">Go to Membership</Button>
                    </>
                )}
                {state.status === "error" && (
                    <>
                        <h1 className="font-serif text-4xl md:text-5xl leading-tight">We couldn't find that session.</h1>
                        <Button onClick={() => navigate("/membership")} className="mt-8 rounded-md text-xs uppercase tracking-[0.3em] h-11 px-6">Return to membership</Button>
                    </>
                )}
            </div>
        </div>
    );
}
